import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { FormFieldCombobox } from '~/components/FormField';
import { useFilterDrawer } from '~/hooks/useFilterDrawer';
import { Drawer } from '~/modules/settings/components/Drawer';
import { EXTERNAL_ROLES } from '~/modules/settings/constants/Filter';
import type { UsersFilter } from '~/modules/settings/types/filter';

const defaultValues: UsersFilter = {
  roles: [],
};

interface LabelUserFormFilterProps {
  open: boolean;
  onApply: () => void;
}

export function LabelUserFormFilter({
  open,
  onApply,
}: LabelUserFormFilterProps) {
  const { t } = useTranslation('settings');

  const filterForm = useForm<UsersFilter>({
    defaultValues,
  });

  const { handleApply, handleClear } = useFilterDrawer<UsersFilter>({
    open,
    onApply,
    filterForm,
  });

  const externalRoles = useMemo(
    () =>
      EXTERNAL_ROLES.map((role) => ({
        value: role.value,
        label: t(role.label),
      })),
    [t],
  );

  return (
    <FormProvider {...filterForm}>
      <Drawer.Header>
        <Drawer.Title>{t('filters')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <FormFieldCombobox
          name="roles"
          label={t('role')}
          multiselect={true}
          displaySelectedOptions={true}
          options={externalRoles}
          placeholder={t('dropdowns.role.placeholder')}
          searchPlaceholder={t('dropdowns.role.searchPlaceholder')}
        />
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={handleClear}
          className="font-normal"
          type="button"
        >
          {t('Clear filters')}
        </Button>
        <Button
          size="large"
          type="button"
          onClick={filterForm.handleSubmit(handleApply)}
        >
          {t('Apply')}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
}

LabelUserFormFilter.displayName = 'LabelUserFormFilter';
